import React from 'react';
import PartnerSearchCell from '../FinanceCalculator/PartnerSearchCell';
import '../FinanceCalculator/FinanceCalculator.css';

const NO_PARTNER_LABELS = ['interest', 'travel', 'other expected expenses'];

const OUTGOING_LINE_TYPES = [
  { value: 'OUTGOING_INVOICE', label: 'Outgoing Invoice' },
  { value: 'CREDIT_NOTE', label: 'Credit Note' },
  { value: 'PROFORMA_INVOICE', label: 'Pro Forma Invoice' },
];

const INCOMING_LINE_TYPES = [
  { value: 'PURCHASE_ORDER', label: 'Purchase Order' },
  { value: 'INCOMING_INVOICE', label: 'Incoming Invoice' },
  { value: 'DEBIT_NOTE', label: 'Debit Note' },
];

const NO_PERCENT_SECTIONS = new Set(['PRODUCT_SALES', 'PRODUCT_PURCHASE']);
const NEGATE_LINE_TYPES = new Set(['CREDIT_NOTE', 'DEBIT_NOTE']);

function shouldShowPartner(line) {
  if (line.is_margin_row || line.is_subtotal) return false;
  const lbl = (line.line_label || '').toLowerCase();
  return !NO_PARTNER_LABELS.some((np) => lbl.includes(np));
}

function getLineTypeOptions(sectionCode) {
  if (sectionCode === 'PRODUCT_SALES') return OUTGOING_LINE_TYPES;
  if (sectionCode === 'PRODUCT_PURCHASE') return INCOMING_LINE_TYPES;
  return null;
}

const BudgetRow = ({ line, sectionCode, quantity, outgoingSubtotal, onChange, onRemove }) => {
  const isPU = line.entry_mode === 'PER_UNIT';
  const isBA = line.entry_mode === 'BUDGET_AMOUNT';
  const hidePercent = NO_PERCENT_SECTIONS.has(sectionCode);
  const lineTypeOptions = getLineTypeOptions(sectionCode);

  const safeNum = (v) => (v == null || v === '' ? 0 : parseFloat(v) || 0);

  const formatDisplay = (val) => {
    if (val == null || val === '') return '0.00';
    return parseFloat(val).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatPercent = (val) => {
    if (val == null || val === '') return '0.00';
    return parseFloat(val).toFixed(2);
  };

  const shouldNegate = NEGATE_LINE_TYPES.has(line.line_type);

  const handleFieldChange = (field, rawValue) => {
    const qty = safeNum(quantity);
    const outSub = safeNum(outgoingSubtotal);
    const sign = shouldNegate ? -1 : 1;
    const updates = { [field]: rawValue };

    if (field === 'amount_per_unit' && isPU) {
      const apu = safeNum(rawValue);
      const effectiveApu = apu * sign;
      const ba = (effectiveApu * qty).toFixed(2);
      updates.budget_amount = ba;
      updates.amount_per_unit = rawValue;
      if (!hidePercent) {
        updates.percent_of_revenue = outSub !== 0 ? ((parseFloat(ba) / outSub) * 100).toFixed(4) : '0';
      }
    } else if (field === 'budget_amount' && isBA) {
      const ba = safeNum(rawValue) * sign;
      updates.budget_amount = rawValue;
      updates.amount_per_unit = qty !== 0 ? (ba / qty).toFixed(4) : '0';
      if (!hidePercent) {
        updates.percent_of_revenue = outSub !== 0 ? ((ba / outSub) * 100).toFixed(4) : '0';
      }
    }

    onChange(line.id, updates);
  };

  const showPartner = shouldShowPartner(line);

  const canRemove = !line.is_system_line || lineTypeOptions;

  const handleLineTypeChange = (newType) => {
    const newNegate = NEGATE_LINE_TYPES.has(newType);
    const wasNegate = NEGATE_LINE_TYPES.has(line.line_type);
    const updates = { line_type: newType };

    if (newNegate !== wasNegate) {
      const apu = safeNum(line.amount_per_unit);
      const qty = safeNum(quantity);
      const sign = newNegate ? -1 : 1;
      updates.budget_amount = (apu * sign * qty).toFixed(2);
    }
    onChange(line.id, updates);
  };

  const showEffectDropdown = sectionCode === 'BELOW_GROSS_ADJUSTMENTS';

  const renderTypeCell = () => {
    if (lineTypeOptions) {
      return (
        <select
          value={line.line_type || ''}
          onChange={(e) => handleLineTypeChange(e.target.value)}
          className="budget-input line-type-select"
        >
          {lineTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    if (line.is_system_line) {
      return <span className="line-label">{line.line_label}</span>;
    }

    return (
      <input
        type="text"
        value={line.line_label || ''}
        onChange={(e) => onChange(line.id, { line_label: e.target.value })}
        className="budget-input label-input"
        placeholder="Row name"
      />
    );
  };

  return (
    <tr className="budget-data-row">
      <td className="type-cell">
        {renderTypeCell()}
      </td>
      {showEffectDropdown && (
        <td className="effect-cell">
          <select
            value={line.net_margin_effect || 'DEDUCT'}
            onChange={(e) => onChange(line.id, { net_margin_effect: e.target.value })}
            className="budget-input effect-select"
          >
            <option value="DEDUCT">Deduct</option>
            <option value="ADD_BACK">Add Back</option>
          </select>
        </td>
      )}
      <td className="partner-cell">
        {showPartner ? (
          <PartnerSearchCell
            value={line.partner_name || ''}
            crmId={line.partner_crm_id}
            onChange={(partnerId, partnerName) => {
              onChange(line.id, { partner_name: partnerName, partner_crm_id: partnerId });
            }}
          />
        ) : null}
      </td>
      <td>
        <textarea
          value={line.description || ''}
          onChange={(e) => onChange(line.id, { description: e.target.value })}
          className="budget-input description-textarea"
          placeholder="Description"
          rows={1}
        />
      </td>
      <td className="amount-cell">
        {isPU ? (
          <input
            type="text"
            inputMode="decimal"
            value={line.amount_per_unit ?? ''}
            onChange={(e) => handleFieldChange('amount_per_unit', e.target.value)}
            className="budget-input amount-input"
            placeholder="0.00"
          />
        ) : (
          <span className="calculated-value">{formatDisplay(line.amount_per_unit)}</span>
        )}
      </td>
      <td className="percent-cell">
        {hidePercent ? null : (
          <span className="calculated-value">{formatPercent(line.percent_of_revenue)}%</span>
        )}
      </td>
      <td className="amount-cell">
        {isBA ? (
          <input
            type="text"
            inputMode="decimal"
            value={line.budget_amount ?? ''}
            onChange={(e) => handleFieldChange('budget_amount', e.target.value)}
            className="budget-input amount-input"
            placeholder="0.00"
          />
        ) : (
          <span className="calculated-value">{formatDisplay(line.budget_amount)}</span>
        )}
      </td>
      <td className="actions-cell">
        {canRemove && (
          <button
            className="remove-row-btn"
            onClick={() => onRemove(line.id)}
            title="Remove row"
          >
            &times;
          </button>
        )}
      </td>
    </tr>
  );
};

export default BudgetRow;
